// Imports for rendering and math utilities from Three.js and related libraries
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";
import { Vector3 } from 'three';
import { DrawingGlobals, LABELS, POLYGONS } from "./variables";
import { LINES, MATERIALS } from "./materials";
import * as THREE from "three";
import Earcut from "earcut";
import { sceneManager } from "../SetUps/SceneManager";

// Creates a CSS2D label to be attached to a 3D object
export function createLabelDiv(className, label) {
    const Div = document.createElement('div');
    Div.className = className;
    let container = new CSS2DObject(Div);
    container.element.innerText = label;
    return container;
}

// Calculates the center of a given geometry (bounding box center)
export function getGeoCenter(geo) {
    geo.geometry.computeBoundingBox();
    geo.geometry.computeVertexNormals();
    geo.geometry.normalizeNormals();
    let center = new Vector3();
    geo.geometry.boundingBox.getCenter(center);
    return center;
}

// Clears out temporary drawing-related data (used in segment manager)
export function cleanUp() {
    DrawingGlobals.intersectionPoints = [];
    DrawingGlobals.lineMeshes = [];
    DrawingGlobals.verticesMesh = [];
    // HISTORY = []; // Intentionally left out, perhaps managed elsewhere
}

// Removes all line meshes from the scene and hides the angle label
export function removeLineMeshes(scene) {
    LINES.referenceLine.visible = false;
    for (let line in DrawingGlobals.lineMeshes) {
        scene.remove(DrawingGlobals.lineMeshes[line]);
        DrawingGlobals.lineMeshes[line].geometry.dispose();
    }
    LABELS.angle.visible = false;
}

// Updates the screen position of a setback label based on an edge midpoint
export function updateSetBacksLabelsPosition(camera, halfWidth, halfHeight, label, edge) {
    const pos = new THREE.Vector3();
    pos.lerpVectors(edge.point1, edge.point2, 0.5); // Midpoint between edge points
    pos.project(camera); // Project to screen space

    if (label) {
        pos.x = (pos.x * halfWidth) + halfWidth;
        pos.y = -(pos.y * halfHeight) + halfHeight;
        label.style.top = `${pos.y}px`;
        label.style.left = `${pos.x}px`;
    }
}

// Triangulates a polygon using the Earcut algorithm and returns a mesh
export function earClipping(vertices) {
    const verticesFlat = vertices.reduce((acc, vertex) => acc.concat([vertex.x, vertex.y, vertex.z]), []);
    const earCutData = Earcut(verticesFlat, null, 3); // 3 = 3D coords
    const indices = new Uint32Array(earCutData);
    const geo = new THREE.BufferGeometry();
    geo.setIndex(new THREE.BufferAttribute(indices, 1));
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verticesFlat), 3));
    return new THREE.Mesh(geo, MATERIALS.polygon.clone());
}

// Creates a wireframe from a list of edges (used for visualizing edges)
export const createEdgesWireframe = (edges, material) => {
    let geometry = new THREE.BufferGeometry().setFromPoints(edges);
    let wireframe = new THREE.LineSegments(geometry, material);
    wireframe.renderOrder = 3;
    wireframe.name = "wireframe";
    return wireframe;
};

// Generates a mathematical plane from the first 3 vertices of a polygon
export function createSegmentPlane(index) {
    let plane = new THREE.Plane();
    let gp = POLYGONS[index].shape.geometry.attributes.position;
    let p1 = new THREE.Vector3().fromBufferAttribute(gp, 0);
    let p2 = new THREE.Vector3().fromBufferAttribute(gp, 1);
    let p3 = new THREE.Vector3().fromBufferAttribute(gp, 2);
    plane.setFromCoplanarPoints(p1, p2, p3);
    // showPlane(p1,p2,p3);
    // plane.translate(new THREE.Vector3(0.1,0.1,0.1));
    return plane;
}

// For debugging: visualize a triangle plane in 3D space
function showPlane(p1, p2, p3) {
    const planeGeometry = new THREE.BufferGeometry();
    const vertices = new Float32Array([
        p1.x, p1.y, p1.z,
        p2.x, p2.y, p2.z,
        p3.x, p3.y, p3.z,
        p1.x, p1.y, p1.z, // Close the loop
    ]);
    planeGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

    const planeMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        side: THREE.DoubleSide,
        wireframe: true
    });

    let planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);
    sceneManager.scene.add(planeMesh);
}


function edgesToBufferGeometry(THREE, edgesGeom) {
  const g = new THREE.BufferGeometry();

  const pos = edgesGeom?.getAttribute?.("position");
  if (pos) g.setAttribute("position", pos.clone());

  // EdgesGeometry is already non-indexed typically; keep it simple.
  g.computeBoundingBox?.();
  g.computeBoundingSphere?.();

  return g;
}

export function sanitizeSceneForReportExport(THREE, scene) {
  // clone(true) keeps transforms/material refs; good enough for export
  const cloned = scene.clone(true);

  cloned.traverse((obj) => {
    if (!obj) return;

    // 🔥 The key: convert EdgesGeometry into normal BufferGeometry
    const geom = obj.geometry;
    if (geom && (geom.type === "EdgesGeometry" || geom.isEdgesGeometry)) {
      obj.geometry = edgesToBufferGeometry(THREE, geom);
      // keep material unchanged
    }
  });

  return cloned;
}
